<script setup>
/**
 * New ticket, without a source conversation.
 *
 * `CreateLinkedTicketDialog` needs a conversation to hang the ticket off - it
 * posts to that conversation's `create_linked_ticket` member route. A ticket
 * started from the tickets screen has no such source, so this dialog creates a
 * conversation stamped as a ticket instead (see the `createTicket` action) and
 * asks for the customer itself.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import {
  createContactSearcher,
  fetchContactableInboxes,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import {
  PRIORITY,
  PRIORITY_META,
  TICKET_TYPE,
  TICKET_TYPE_META,
} from 'dashboard/helper/conversationLens';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const emit = defineEmits(['created']);

const { t } = useI18n();
const store = useStore();
const searchContacts = createContactSearcher();

const dialogRef = ref(null);
const contactId = ref('');
const contactLabel = ref('');
const contacts = ref([]);
const contactableInboxes = ref([]);
const isSearching = ref(false);
const inboxId = ref('');
const subject = ref('');
const priority = ref('');
const assigneeId = ref('');
const ticketType = ref(TICKET_TYPE.CUSTOMER);
const isCreating = ref(false);

const accountId = useMapGetter('getCurrentAccountId');
const currentUserId = useMapGetter('getCurrentUserID');
const agents = useMapGetter('agents/getVerifiedAgents');

const contactOptions = computed(() =>
  contacts.value.map(contact => ({
    value: contact.id,
    label: contact.email ? `${contact.name} (${contact.email})` : contact.name,
  }))
);

const inboxOptions = computed(() =>
  contactableInboxes.value.map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  }))
);

const priorityOptions = computed(() => [
  { value: '', label: t('TICKETS.PRIORITY.NONE') },
  ...Object.values(PRIORITY).map(value => ({
    value,
    label: t(PRIORITY_META[value].labelKey),
  })),
]);

const agentOptions = computed(() =>
  agents.value.map(agent => ({ value: agent.id, label: agent.name }))
);

const typeOptions = computed(() =>
  Object.values(TICKET_TYPE).map(value => ({
    value,
    label: t(TICKET_TYPE_META[value].labelKey),
  }))
);

const selectedInbox = computed(() =>
  contactableInboxes.value.find(inbox => inbox.id === inboxId.value)
);

const isSubmitDisabled = computed(
  () => !contactId.value || !inboxId.value || !subject.value.trim()
);

const onContactSearch = debounce(
  async query => {
    isSearching.value = true;
    try {
      const results = await searchContacts(query);
      // null means a newer search aborted this one.
      if (results !== null) contacts.value = results;
    } catch (error) {
      useAlert(t('TICKETS.CREATE.CONTACT_ERROR'));
    } finally {
      isSearching.value = false;
    }
  },
  400,
  false
);

// The inbox list is the contact's own reachable channels: each one carries the
// `source_id` the conversation needs.
const onContactSelect = async id => {
  contactId.value = id;
  contactLabel.value =
    contactOptions.value.find(option => option.value === id)?.label ?? '';
  inboxId.value = '';
  contactableInboxes.value = id ? await fetchContactableInboxes(id) : [];
  if (contactableInboxes.value.length === 1)
    inboxId.value = contactableInboxes.value[0].id;
};

const open = () => {
  contactId.value = '';
  contactLabel.value = '';
  contacts.value = [];
  contactableInboxes.value = [];
  inboxId.value = '';
  subject.value = '';
  priority.value = '';
  ticketType.value = TICKET_TYPE.CUSTOMER;
  assigneeId.value = currentUserId.value;
  store.dispatch('agents/get');
  dialogRef.value.open();
};

const onConfirm = async () => {
  if (isSubmitDisabled.value || isCreating.value) return;
  isCreating.value = true;
  try {
    const data = await store.dispatch('createTicket', {
      inboxId: inboxId.value,
      contactId: contactId.value,
      sourceId: selectedInbox.value.sourceId,
      assigneeId: assigneeId.value,
      subject: subject.value.trim(),
      priority: priority.value || null,
      ticketType: ticketType.value,
    });
    dialogRef.value.close();
    emit('created', data.id);
    useAlert(t('TICKETS.CREATE.SUCCESS'), {
      type: 'link',
      to: `/app/accounts/${accountId.value}/tickets/${data.id}`,
      message: t('TICKETS.CREATE.GO_TO_TICKET'),
    });
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TICKETS.CREATE.ERROR'));
  } finally {
    isCreating.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('TICKETS.CREATE.TITLE')"
    :confirm-button-label="t('TICKETS.CREATE.SUBMIT')"
    :disable-confirm-button="isSubmitDisabled"
    :is-loading="isCreating"
    overflow-y-auto
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TICKETS.CREATE.CONTACT_LABEL') }}
        </label>
        <ComboBox
          :model-value="contactId"
          data-testid="new-ticket-contact"
          use-api-results
          :options="contactOptions"
          :display-label="contactLabel"
          :placeholder="t('TICKETS.CREATE.CONTACT_PLACEHOLDER')"
          :search-placeholder="t('TICKETS.CREATE.CONTACT_PLACEHOLDER')"
          :empty-state="
            isSearching
              ? t('TICKETS.CREATE.CONTACT_SEARCHING')
              : t('TICKETS.CREATE.CONTACT_EMPTY')
          "
          @search="onContactSearch"
          @update:model-value="onContactSelect"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TICKETS.CREATE.INBOX_LABEL') }}
        </label>
        <ComboBox
          v-model="inboxId"
          data-testid="new-ticket-inbox"
          :options="inboxOptions"
          :disabled="!contactId"
          :placeholder="t('TICKETS.CREATE.INBOX_PLACEHOLDER')"
          :empty-state="t('TICKETS.CREATE.INBOX_EMPTY')"
        />
      </div>

      <Input
        v-model="subject"
        data-testid="new-ticket-subject"
        :label="t('TICKETS.CREATE.SUBJECT_LABEL')"
        :placeholder="t('TICKETS.CREATE.SUBJECT_PLACEHOLDER')"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TICKETS.CREATE.TYPE_LABEL') }}
        </label>
        <Select
          v-model="ticketType"
          data-testid="new-ticket-type"
          :options="typeOptions"
        />
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TICKETS.CREATE.PRIORITY_LABEL') }}
          </label>
          <Select
            v-model="priority"
            data-testid="new-ticket-priority"
            :options="priorityOptions"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TICKETS.CREATE.ASSIGNEE_LABEL') }}
          </label>
          <Select
            v-model="assigneeId"
            data-testid="new-ticket-assignee"
            :options="agentOptions"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
